import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import componentStyles from './passwordReset.module.css';
import globalStyles from '../globals.module.css';
import NotificationModel from '../../model/notificationModel';
import { 
    NOTIFICATIONS_SUCCESS, 
    NOTIFICATIONS_ERROR, 
    EVENT_NOTIFICATION_PUSH,
    EVENT_UPDATE_ACTION_BAR_VIEW
} from '../../utils/constants';
import eventBus from '../../service/eventBus';
import { resetPassword } from '../../service/userService';
import PageView from '../common/pageView';

const PasswordResetConfirm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const token = searchParams.get('token');

    // Add effect to handle the view change after navigation
    useEffect(() => {
        // Check if we're coming from a successful password reset
        const isResetSuccess = sessionStorage.getItem('passwordResetSuccess');
        if (isResetSuccess) {
            // Clear the flag
            sessionStorage.removeItem('passwordResetSuccess');
            // Trigger the login view
            eventBus.emit(EVENT_UPDATE_ACTION_BAR_VIEW, 'login');
        }
    }, []);

    const validatePassword = () => {
        if (password.length < 8) {
            setError('Password must be at least 8 characters long');
            return false;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!validatePassword()) {
            return;
        }

        setIsLoading(true);

        try {
            const response = await resetPassword(token, password);
            const { data } = response;

            if (data.success) {
                eventBus.emit(EVENT_NOTIFICATION_PUSH, 
                    new NotificationModel('Password has been reset successfully!', NOTIFICATIONS_SUCCESS)
                );
                
                // Set a flag in session storage to indicate successful reset
                sessionStorage.setItem('passwordResetSuccess', 'true');
                
                // Navigate to home page
                navigate('/home');
            } else {
                eventBus.emit(EVENT_NOTIFICATION_PUSH, 
                    new NotificationModel(data.error || 'Failed to reset password. Please try again.', NOTIFICATIONS_ERROR)
                );
            }
        } catch (error) {
            eventBus.emit(EVENT_NOTIFICATION_PUSH, 
                new NotificationModel('Failed to reset password. Please try again.', NOTIFICATIONS_ERROR)
            );
            console.error('Password reset error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToLogin = () => {
        eventBus.emit(EVENT_UPDATE_ACTION_BAR_VIEW, 'login');
    };

    const renderContent = () => {
        if (!token) {
            return (
                <PageView title="Password Recovery" contentClassName={componentStyles.resetContainer}>
                    <section className={`${globalStyles.standardSection} ${componentStyles.resetCard}`}>
                        <div className={componentStyles.panelLabel}>Invalid token // action required</div>
                        <p className={componentStyles.resetDescription}>
                            This password reset link is invalid or has expired. Please request a new password reset link.
                        </p>
                        <div className={componentStyles.loginPrompt}>
                            <button 
                                onClick={handleBackToLogin}
                                className={globalStyles.primaryButton}
                            >
                                Back to Login
                            </button>
                        </div>
                    </section>
                </PageView>
            );
        }

        return (
            <PageView title="Set New Password" contentClassName={componentStyles.resetContainer}>
                <section className={`${globalStyles.standardSection} ${componentStyles.resetCard}`}>
                    <div className={componentStyles.panelLabel}>Account security // new credentials</div>
                    <p className={componentStyles.resetDescription}>
                        Please enter your new password below.
                    </p>
                    
                    <form onSubmit={handleSubmit} className={globalStyles.form}>
                        <div className={globalStyles.formGroup}>
                            <label>New Password</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter new password"
                                required
                                className={globalStyles.standardInput}
                                minLength="8"
                            />
                        </div>
                        
                        <div className={globalStyles.formGroup}>
                            <label>Confirm New Password</label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm new password"
                                required
                                className={globalStyles.standardInput}
                                minLength="8"
                            />
                        </div>

                        {error && (
                            <div className={componentStyles.errorMessage}>
                                {error}
                            </div>
                        )}
                        
                        <button 
                            type="submit" 
                            className={globalStyles.primaryButton}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Resetting...' : 'Reset Password'}
                        </button>
                    </form>

                    <div className={componentStyles.loginPrompt}>
                        <p>Remember your password?</p>
                        <button 
                            onClick={handleBackToLogin}
                            className={globalStyles.secondaryButton}
                        >
                            Back to Login
                        </button>
                    </div>
                </section>
            </PageView>
        );
    };

    return renderContent();
};

export default PasswordResetConfirm;
